// src/encounter/lib/use-stable-chart-order.ts
// The rail-v3 rows in the ruled order within each section (`sortSection`; encounter owner ruling 4, 1 Nov, D-5315; left-rail
// target 4 Nov §2), FROZEN for the session: an entry keeps the place it had the first time it was seen, so nothing jumps under
// the clinician's pointer while the visit runs (a write, a refetch, a record arriving). A new entry goes in at its sorted place.
// The freeze is per patient: another patient starts again from the sorted order. Ported unchanged from
// origin/claude/rail-v3-behaviour (1a1e5283, a354637d, 4daa0854), where it sat behind the `chartRailV3` flag.
//
// A section is frozen only once its sort facts have SETTLED (the full chart records loaded, or failed, or are not read on
// this host): freezing on the summary's first paint would lock the wire order in before the dates arrive. Until then the
// section shows its sorted order of the moment.
//
// The frozen orders are state, adjusted DURING render when they change (React's documented alternative to an effect; the
// second pass finds nothing new and stops).
import { useState } from 'react';
import type { IpsSection } from '@scripsteam/scrips-signal-ds';
import { mergeStableOrder, orderEntries, sortSection, type ChartSortFacts } from './chart-column-model';

export interface ChartSectionSortInput {
  /** The row facts by entry id (dates, criticality), from the full chart records. Absent = none known. */
  records?: ReadonlyMap<string, ChartSortFacts>;
  /** The facts will not change any more (loaded, failed, or never read here): the section may be frozen. */
  settled: boolean;
  /** The full-record query is refetching (after a write the summary and the records refetch independently). A NEW row
   *  whose record is not in `records` yet is then kept out of the freeze until its facts arrive. */
  fetching?: boolean;
}

interface Frozen {
  patientId: string | undefined;
  orders: Readonly<Record<string, readonly string[]>>;
}

const sameIds = (a: readonly string[] | undefined, b: readonly string[]) =>
  !!a && a.length === b.length && a.every((id, i) => id === b[i]);

/**
 * The sections with their entries in the session-stable order. `enabled` false returns `sections` unchanged (the legacy
 * rail: the wire order, exactly as before).
 */
export function useStableChartOrder(
  patientId: string | undefined,
  enabled: boolean,
  sections: readonly IpsSection[],
  input: (sectionId: string) => ChartSectionSortInput,
): readonly IpsSection[] {
  const [frozen, setFrozen] = useState<Frozen>({ patientId, orders: {} });
  if (!enabled) return sections;
  const samePatient = frozen.patientId === patientId;
  const base = samePatient ? frozen.orders : {};
  const next: Record<string, readonly string[]> = { ...base };
  const out: IpsSection[] = [];
  const grown: string[] = [];
  for (const s of sections) {
    const { records, settled, fetching } = input(s.id);
    const sorted = sortSection(s.id, s.entries, records);
    if (!settled) {
      // Not settled (first load, or a refetch after the freeze). Once frozen, the frozen order always wins: rows already
      // placed never move while loading, and new rows only join (at the end for now) once their facts are settled.
      const frozenIds = base[s.id];
      out.push({ ...s, entries: frozenIds ? orderEntries(sorted, frozenIds) : sorted });
      continue;
    }
    const ids = sorted.filter((e) => e.id !== `${s.id}-more`).map((e) => e.id);
    // A new row whose record is still on its way has no date yet: freezing it now would pin it where a missing date sorts
    // (last) for the whole session. It is shown at its sorted place of the moment and frozen once its facts exist. Rows
    // already placed are never held back, so they never move.
    const placed = new Set(base[s.id] ?? []);
    const waiting = (id: string) => !!fetching && !!records && !records.has(id) && !placed.has(id);
    const merged = mergeStableOrder(base[s.id], ids.filter((id) => !waiting(id)));
    if (!sameIds(base[s.id], merged)) {
      next[s.id] = merged;
      grown.push(s.id);
    }
    out.push({ ...s, entries: orderEntries(sorted, mergeStableOrder(merged, ids)) });
  }
  if (!samePatient || grown.length > 0) setFrozen({ patientId, orders: next });
  return out;
}
